import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  ChevronLeftIcon,
  ChevronRight,
  DoubleLeftIcon,
  DoubleRightIcon,
} from "../../assets/icons";
import { combineClassNames } from "../../utils/appUtils";
import IconButton from "../IconButton";

import "./WPTabs.scss";

const CLIP_DETECTION_TOLERANCE = 0.5;

const WPTabs = ({ tabs, className, children, onTabChange, activeTabIndex }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const tabsMeasureRef = useRef(null);
  const tabsViewportRef = useRef(null);
  const tabButtonRefs = useRef([]);
  const [internalActiveTab, setInternalActiveTab] = useState(0);
  const [showTabPagination, setShowTabPagination] = useState(false);

  const isControlled = typeof activeTabIndex === "number";
  const activeTab = isControlled
    ? Math.min(Math.max(activeTabIndex, 0), Math.max(0, tabs.length - 1))
    : internalActiveTab;

  const childNodes = React.Children.toArray(children);

  const recalculateVisibleTabs = useCallback(() => {
    const containerWidth = containerRef.current?.offsetWidth || 0;
    const measuredTabElements = tabsMeasureRef.current?.children
      ? Array.from(tabsMeasureRef.current.children)
      : [];

    if (!containerWidth || !measuredTabElements.length) return;

    const measuredTabWidths = measuredTabElements.map((tabElement) =>
      Math.ceil(tabElement.getBoundingClientRect().width),
    );

    const totalTabsWidth = measuredTabWidths.reduce(
      (total, currentWidth) => total + currentWidth,
      0,
    );

    setShowTabPagination(totalTabsWidth > containerWidth);
  }, []);

  const scrollTabIntoView = useCallback((index) => {
    const viewport = tabsViewportRef.current;
    const tabButton = tabButtonRefs.current[index];

    if (!viewport || !tabButton) return;

    const viewportBounds = viewport.getBoundingClientRect();
    const tabBounds = tabButton.getBoundingClientRect();
    const isLeftClipped =
      tabBounds.left < viewportBounds.left - CLIP_DETECTION_TOLERANCE;
    const isRightClipped =
      tabBounds.right > viewportBounds.right + CLIP_DETECTION_TOLERANCE;

    if (!isLeftClipped && !isRightClipped) {
      return;
    }

    tabButton.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: isLeftClipped ? "start" : "end",
    });
  }, []);

  const activateTab = useCallback(
    (index) => {
      if (index < 0 || index >= tabs.length || tabs[index].disabled) {
        return;
      }

      if (!isControlled) {
        setInternalActiveTab(index);
      }

      if (onTabChange) {
        onTabChange(index);
      }
    },
    [isControlled, onTabChange, tabs],
  );

  const findEnabledIndex = useCallback(
    (start, end, step) => {
      for (let index = start; index !== end; index += step) {
        if (!tabs[index]?.disabled) {
          return index;
        }
      }

      return -1;
    },
    [tabs],
  );

  const firstEnabledTabIndex = useMemo(
    () => findEnabledIndex(0, tabs.length, 1),
    [findEnabledIndex, tabs.length],
  );

  const lastEnabledTabIndex = useMemo(
    () => findEnabledIndex(tabs.length - 1, -1, -1),
    [findEnabledIndex, tabs.length],
  );

  const previousEnabledTabIndex = useMemo(
    () => findEnabledIndex(activeTab - 1, -1, -1),
    [activeTab, findEnabledIndex],
  );

  const nextEnabledTabIndex = useMemo(
    () => findEnabledIndex(activeTab + 1, tabs.length, 1),
    [activeTab, findEnabledIndex, tabs.length],
  );

  const activateAndRevealTab = useCallback(
    (index) => {
      if (index < 0) return;

      activateTab(index);
      scrollTabIntoView(index);
    },
    [activateTab, scrollTabIntoView],
  );

  const handleTabClick = (index) => {
    activateAndRevealTab(index);

    if (!showTabPagination) {
      return;
    }

    // Run a second pass on next frame to account for post-click layout shifts.
    requestAnimationFrame(() => {
      scrollTabIntoView(index);
    });
  };

  const goToFirstTabSet = useCallback(() => {
    activateAndRevealTab(firstEnabledTabIndex);
  }, [activateAndRevealTab, firstEnabledTabIndex]);

  const goToPreviousTabSet = useCallback(() => {
    activateAndRevealTab(previousEnabledTabIndex);
  }, [activateAndRevealTab, previousEnabledTabIndex]);

  const goToNextTabSet = useCallback(() => {
    activateAndRevealTab(nextEnabledTabIndex);
  }, [activateAndRevealTab, nextEnabledTabIndex]);

  const goToLastTabSet = useCallback(() => {
    activateAndRevealTab(lastEnabledTabIndex);
  }, [activateAndRevealTab, lastEnabledTabIndex]);

  useLayoutEffect(() => {
    recalculateVisibleTabs();

    window.addEventListener("resize", recalculateVisibleTabs);
    return () => {
      window.removeEventListener("resize", recalculateVisibleTabs);
    };
  }, [recalculateVisibleTabs, tabs]);

  useEffect(() => {
    setInternalActiveTab((prev) =>
      Math.min(prev, Math.max(0, tabs.length - 1)),
    );
  }, [tabs.length]);

  useEffect(() => {
    tabButtonRefs.current = tabButtonRefs.current.slice(0, tabs.length);
  }, [tabs.length]);

  useEffect(() => {
    if (!showTabPagination) {
      const viewport = tabsViewportRef.current;
      if (viewport) {
        viewport.scrollLeft = 0;
      }
      return;
    }

    scrollTabIntoView(activeTab);
  }, [activeTab, scrollTabIntoView, showTabPagination]);

  const getClassName = useCallback(
    (index) =>
      combineClassNames(
        "tab fs-16 font-notosans",
        activeTab === index && "active",
      ),
    [activeTab],
  );

  const isAtStart = activeTab === firstEnabledTabIndex;
  const isAtEnd = activeTab === lastEnabledTabIndex;

  const tabControlTooltip = {
    first: t("navigationLabels.goToFirst"),
    previous: t("navigationLabels.previous"),
    next: t("navigationLabels.next"),
    last: t("navigationLabels.goToLast"),
  };

  return (
    <div
      ref={containerRef}
      className={combineClassNames(
        "wp-tab-container",
        className,
        showTabPagination && "wp-tab-container--paginated",
      )}
    >
      <div
        ref={tabsMeasureRef}
        className="tabs tabs--measure"
        aria-hidden="true"
      >
        {tabs.map((tab, index) => (
          <button
            key={`measure-${tab.id || index}`}
            type="button"
            className="tab fs-16 font-notosans"
          >
            {tab.title}
          </button>
        ))}
      </div>

      <div className="tabs-navigation-row">
        {showTabPagination && (
          <div
            className="tabs-nav-controls tabs-nav-controls--start"
            aria-label="Tab pagination controls"
          >
            <IconButton
              className="tabs-nav-control"
              onClick={goToFirstTabSet}
              disabled={isAtStart}
              tooltip={tabControlTooltip.first}
              id="tab-go-to-first"
            >
              <DoubleLeftIcon />
            </IconButton>
            <IconButton
              className="tabs-nav-control"
              onClick={goToPreviousTabSet}
              disabled={previousEnabledTabIndex < 0}
              tooltip={tabControlTooltip.previous}
              id="tab-go-to-previous"
            >
              <ChevronLeftIcon />
            </IconButton>
          </div>
        )}

        <div ref={tabsViewportRef} className="tabs">
          {tabs.map((tab, index) => (
            <button
              ref={(element) => {
                tabButtonRefs.current[index] = element;
              }}
              id={tab.id}
              key={tab.id || index}
              disabled={tab.disabled}
              className={getClassName(index)}
              onClick={() => handleTabClick(index)}
            >
              {tab.title}
            </button>
          ))}
        </div>

        {showTabPagination && (
          <div
            className="tabs-nav-controls tabs-nav-controls--end"
            aria-label="Tab pagination controls"
          >
            <IconButton
              className="tabs-nav-control"
              onClick={goToNextTabSet}
              disabled={nextEnabledTabIndex < 0}
              tooltip={tabControlTooltip.next}
              id="tab-go-to-next"
            >
              <ChevronRight />
            </IconButton>
            <IconButton
              className="tabs-nav-control"
              onClick={goToLastTabSet}
              disabled={isAtEnd}
              tooltip={tabControlTooltip.last}
              id="tab-go-to-last"
            >
              <DoubleRightIcon />
            </IconButton>
          </div>
        )}
      </div>
      <div className="tab-content">
        {tabs[activeTab] && childNodes[activeTab]}
      </div>
    </div>
  );
};

export default WPTabs;
